import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  input,
  Output,
  QueryList,
  signal,
  ViewChildren
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AutoFocusNextDirective } from './directives/auto-focus-next.directive';
import { AutoDeletePreviousDirective } from './directives/focus-previous.directive';
import { OnlyNumbersDirective } from '@pc/input';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { Day, IDate, Month, Year } from './interfaces/date.interface';
import { DateUtils } from './utils/date-utils';
import { AutoFocusFirstEmptyDirective } from './directives/auto-focus-first-empty.directive';
import { BusinessUnit, Color } from '@pc/common';

@Component({
  selector: 'pc-date',
  standalone: true,
  imports: [
    CommonModule,
    AutoDeletePreviousDirective,
    AutoFocusNextDirective,
    AutoFocusFirstEmptyDirective,
    OnlyNumbersDirective,
    FormsModule,
    ReactiveFormsModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateComponent),
      multi: true
    }
  ],
  templateUrl: './date.component.html',
  styleUrl: './date.component.scss'
})
export class DateComponent implements ControlValueAccessor {
  @Input() dayLabel = '';
  @Input() monthLabel = '';
  @Input() yearLabel = '';
  @Input() labelId!: string;
  @Input() dateContext = '';
  @Output() blurEvent = new EventEmitter<void>();
  @Output() focusinEvent = new EventEmitter<void>();
  @Output() inputEvent = new EventEmitter<void>();
  @ViewChildren('inputField') inputFields!: QueryList<ElementRef>;
  isAutoFocus = input(false);
  businessUnit = input(BusinessUnit.IL);
  placeholder = input('');
  isMandatory = input(true);
  asteriskPosition = input<'before' | 'after'>('before');
  disabled = input(false);

  public showBorderError = input(false);
  public borderColor = Color.INPUT_ERROR;
  public BusinessUnit = BusinessUnit;

  days = signal<Day[]>([
    { key: 'day1', value: '' },
    { key: 'day2', value: '' }
  ]);

  months = signal<Month[]>([
    { key: 'month1', value: '' },
    { key: 'month2', value: '' }
  ]);

  years = signal<Year[]>([
    { key: 'year1', value: '' },
    { key: 'year2', value: '' },
    { key: 'year3', value: '' },
    { key: 'year4', value: '' }
  ]);

  private dateUtils = inject(DateUtils);
  private elementRef = inject(ElementRef);

  constructor() {
    effect(() => {
      if (this.isAutoFocus() && this.inputFields && this.inputFields.first) {
        this.inputFields.first.nativeElement.focus();
      }
    });
  }

  resetDate(): void {
    this.days.set([
      { key: 'day1', value: '' },
      { key: 'day2', value: '' }
    ]);

    this.months.set([
      { key: 'month1', value: '' },
      { key: 'month2', value: '' }
    ]);

    this.years.set([
      { key: 'year1', value: '' },
      { key: 'year2', value: '' },
      { key: 'year3', value: '' },
      { key: 'year4', value: '' }
    ]);
  }

  initDate(d: IDate) {
    this.days.set(d.day.split('').map((digit, index) => ({ key: `${index}`, value: digit })));
    this.months.set(d.month.split('').map((digit, index) => ({ key: `${index}`, value: digit })));
    this.years.set(d.year.split('').map((digit, index) => ({ key: `${index}`, value: digit })));
  }

  handleFocusout(event: FocusEvent): void {
    if (!this.elementRef.nativeElement.contains(event.relatedTarget)) {
      this.setDefaultDate('days');
      this.setDefaultDate('months');
      this.blurEvent.emit();
    }
  }

  public handleFocusin(): void {
    this.focusinEvent.emit();
  }

  public combineDate(): void {
    const date = {
      day: this.dateUtils.combineDays(this.days()),
      month: this.dateUtils.combineMonths(this.months()),
      year: this.dateUtils.combineYears(this.years())
    };
    this.onChange(date);
    this.inputEvent.emit();
  }

  public writeValue(value: IDate): void {
    if (value) {
      this.initDate(value);
    } else {
      this.resetDate();
    }
    return;
  }

  public registerOnChange(fn: (value: IDate) => void): void {
    this.onChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/naming-convention
  public registerOnTouched(_fn: () => void): void {
    return;
  }

  setDisabledState?(): void {
    return;
  }

  public setDefaultDate(dateInput: 'days' | 'months'): void {
    const dateValueHandler = setTimeout(() => {
      const dateValue = dateInput === 'days' ? this.days : this.months;
      // Case 1: dateValue1 is empty and dateValue2 is not empty -> padding dateValue1 with 0 and keeping dateValue2 as is
      if (dateValue()[0].value === '' && dateValue()[1].value !== '') {
        dateValue.set([
          { key: dateValue()[0].key, value: '0' },
          { key: dateValue()[1].key, value: dateValue()[1].value }
        ]);
      }

      // Case 2: dateValue1 is not empty and dateValue2 is empty -> moving dateValue1 to dateValue2 and padding dateValue1 with 0
      if (dateValue()[0].value !== '' && dateValue()[0].value !== '0' && dateValue()[1].value === '') {
        dateValue.set([
          { key: dateValue()[0].key, value: '0' },
          { key: dateValue()[1].key, value: dateValue()[0].value }
        ]);
      }

      clearTimeout(dateValueHandler);
    }, 0);
  }

  public navigateDateInput(event: KeyboardEvent, index: number) {
    const inputs = this.inputFields.toArray();
    const inputLength = inputs.length;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      if (index < inputLength - 1) {
        event.preventDefault();
        const nextInput = inputs[index + 1].nativeElement;
        nextInput.focus();
        nextInput.setSelectionRange(nextInput.value.length, nextInput.value.length);
      }
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      if (index > 0) {
        event.preventDefault();
        const prevInput = inputs[index - 1].nativeElement;
        prevInput.focus();
        prevInput.setSelectionRange(prevInput.value.length, prevInput.value.length);
      }
    }
  }

  private onChange: (value: IDate) => void = () => {
    return;
  };
}
